/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { colors } from "@farmlands/mui-theme";
import { Box, Collapse, styled, SxProps, Theme, Typography } from "@mui/material";
import React from "react";

import { TestIds } from "../testIds";

import { ContactItem } from "./ContactCard";
import MaterialSymbol from "./MaterialSymbol";

export const HelperCollapse = styled(Collapse)(({ theme }) => ({
  background: theme.colors.primary100,
  color: theme.colors.primary500,
}));

export const CollapseContainer = styled("div")`
  display: flex;
  flex-direction: column;
  margin: 16px;
  padding-left: 16px;
  border-left: 4px solid ${colors.primary500};
`;

interface HelperMessageProps {
  title?: string;
  sx?: SxProps<Theme>;
  body: string;
  show: boolean;
  name?: string | null;
  phone?: string | null;
  email?: string | null;
}
const HelperMessage: React.FC<HelperMessageProps> = ({
  sx,
  title,
  body,
  show,
  name,
  phone,
  email,
}) => {
  return (
    <HelperCollapse in={show} data-testid={TestIds.components.helperMessage.container}>
      <CollapseContainer sx={sx}>
        {title && (
          <Typography color={colors.primary700} variant="body2" fontWeight="bold">
            {title}
          </Typography>
        )}

        <Typography
          color={colors.primary700}
          variant="body2"
          component={"p"}
          sx={{ whiteSpace: "pre-line" }}
          fontWeight="regular"
          data-testid={TestIds.components.helperMessage.body}
          gutterBottom={name == undefined || phone == undefined || email == undefined}
        >
          {body}
        </Typography>

        {name && (
          <Typography
            color={colors.primary700}
            variant="body2"
            component={"p"}
            fontWeight="regular"
            gutterBottom={phone == undefined || email == undefined}
          >
            {name}
          </Typography>
        )}

        <Box>
          {phone && (
            <ContactItem color={colors.primary700} href={`tel:${phone}`}>
              <MaterialSymbol>phone</MaterialSymbol>
              <Typography variant="body2" color={"inherit"} sx={{ textDecoration: "underline" }}>
                {phone}
              </Typography>
            </ContactItem>
          )}

          {email && (
            <ContactItem color={colors.primary700} href={`mailto:${email}`}>
              <MaterialSymbol>email</MaterialSymbol>
              <Typography variant="body2" color={"inherit"} sx={{ textDecoration: "underline" }}>
                {email}
              </Typography>
            </ContactItem>
          )}
        </Box>
      </CollapseContainer>
    </HelperCollapse>
  );
};

export default HelperMessage;
